import { formatShortDateWithWeekday } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import {
  type PlacementCandidate,
  PlacementCandidateScreeningStatus,
} from "@src/appV2/redesign/PlacementCandidate/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { parseISO } from "date-fns";
import { startCase } from "lodash";

import { type PlacementWithDetails } from "../../../api/useFetchPaginatedPlacements";
import { ListHelper } from "./ListHelper";
import { PlacementSectionTemplate } from "./PlacementSectionTemplate";
import { TalkToRecruiterInterviewAvailabilityBullet } from "./TalkToRecruiterInterviewAvailabilityBullet";

interface InterviewAvailabilitySectionProps {
  placement: PlacementWithDetails;
  placementCandidate: PlacementCandidate;
}

export function InterviewAvailabilitySection(props: InterviewAvailabilitySectionProps) {
  const { placement, placementCandidate } = props;
  const worker = useDefinedWorker();

  const { allowRequestInterview, interviewSettings, hiringEvents, isRecruiterScreeningEnabled } =
    placement;
  const interviewAvailability = interviewSettings?.attributes.availability;

  const hiringEventDays = isDefined(hiringEvents)
    ? getHiringEventDays(hiringEvents, worker.tmz)
    : [];
  const availableDays = isDefined(interviewAvailability)
    ? getAvailableDays(interviewAvailability)
    : [];
  const formattedAvailableDays = formatAvailabilityStrings(availableDays);

  return (
    <PlacementSectionTemplate title="Interview availability" iconType="bolt">
      <ListHelper>
        {hiringEventDays.map((day: string) => (
          <ListHelper.Item key={day}>
            <ListHelper.Text>{`Taking interviews on ${day}`}</ListHelper.Text>
          </ListHelper.Item>
        ))}
        {availableDays.length > 0 && (
          <ListHelper.Item>
            <ListHelper.Text>{`Taking interviews on ${formattedAvailableDays}`}</ListHelper.Text>
          </ListHelper.Item>
        )}
        {allowRequestInterview && (
          <ListHelper.Item>
            <ListHelper.Text>
              Provide your availability dates for an in-person interview and the workplace will
              reach out to you within 3 business days
            </ListHelper.Text>
          </ListHelper.Item>
        )}
        {isRecruiterScreeningEnabled &&
          (placementCandidate.screeningStatus === PlacementCandidateScreeningStatus.NO_SCREENING ||
            placementCandidate.screeningStatus === PlacementCandidateScreeningStatus.FAILED) && (
            <TalkToRecruiterInterviewAvailabilityBullet
              placement={placement}
              placementCandidate={placementCandidate}
            />
          )}
      </ListHelper>
    </PlacementSectionTemplate>
  );
}

function getAvailableDays(availability: Array<{ dayOfWeek: string }>): string[] {
  const allAvailabilityStrings = availability.map((avail) => {
    const day = avail.dayOfWeek.toLowerCase();
    return startCase(day);
  });
  return [...new Set(allAvailabilityStrings)];
}

function getHiringEventDays(
  hiringEvent: NonNullable<PlacementWithDetails["hiringEvents"]>,
  workerTmz: string
) {
  return hiringEvent
    .map((event) => {
      return isDefined(event.attributes.fixedSchedule)
        ? formatShortDateWithWeekday(parseISO(event.attributes.fixedSchedule.startDate), {
            timeZone: workerTmz,
          })
        : undefined;
    })
    .filter(isDefined);
}

function formatAvailabilityStrings(days: string[]): string {
  const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  const sortedDays = days.sort((a, b) => {
    const aIndex = DAY_ORDER.indexOf(a);
    const bIndex = DAY_ORDER.indexOf(b);
    return aIndex - bIndex;
  });

  if (sortedDays.length === 1) {
    return days[0];
  }

  const lastDay = sortedDays.pop()!;
  return `${sortedDays.join(", ")} & ${lastDay}`;
}

export function hasInterviewAvailabilityEnabled(placement?: PlacementWithDetails): boolean {
  if (!placement) {
    return false;
  }

  const { allowRequestInterview, interviewSettings, hiringEvents } = placement;
  const hasAvailability =
    Boolean(interviewSettings?.attributes.scheduleInterviews) &&
    isDefined(interviewSettings?.attributes.availability) &&
    interviewSettings.attributes.availability.length > 0;

  const hasHiringEvents = isDefined(hiringEvents) && hiringEvents.length > 0;

  return hasAvailability || hasHiringEvents || (allowRequestInterview ?? false);
}
